import {
  Backdrop,
  ButtonTertiary,
  cssColorValue,
  IconX,
  screenMdMin,
  screenXsMin,
  semanticColors,
  spacer16,
  spacer4,
  spacer40,
} from '@spotify-internal/encore-web';
import React, { useRef } from 'react';
import styled from 'styled-components';

import { useTranslation } from 'providers/translations/TranslationProvider';
import { MastheadHeaderData } from 'lib/sanity/types';
import { useCustomer } from 'utils/user';
import { useBackdropMenu } from 'components/Masthead/Header/hooks/useBackdropMenu';
import { NavBarSideMenuOpenButton } from 'components/Masthead/Header/SideMenu/OpenButton';
import { ProfileSideBar } from 'components/Masthead/Header/Profile/SideBar';
import { NavBarSideMenu } from 'components/Masthead/Header/SideMenu/Menu';
import { useServerSideConstants } from 'providers/ServerSideConstantsProvider';

const MenuButtonWrapper = styled.div`
  display: none;
  @media (max-width: ${screenMdMin}) {
    display: flex;
    position: relative;
  }
`;

const CloseMenuButton = styled(ButtonTertiary)`
  color: ${cssColorValue(semanticColors.textBase)};
  padding-inline-end: 0;
  position: absolute;
  inset-inline-end: ${spacer40};
  top: ${spacer4};
  z-index: 1021;
  &:hover {
    color: ${cssColorValue(semanticColors.textBase)};
    cursor: pointer;
  }
  &::after {
    display: none;
  }
  @media (max-width: ${screenXsMin}) {
    inset-inline-end: ${spacer16};
  }
`;

type Props = {
  headerEntry: MastheadHeaderData;
};

export const NavBarSideMenuWrapper = ({ headerEntry }: Props) => {
  const backdropRef = useRef(null);
  const { t } = useTranslation();
  const {
    verticalConfig: { theme },
    market: { dir },
  } = useServerSideConstants();
  const { handleOnKeyDown, closeBackdrop, openBackdrop, backdropOpen, closeBtnRef, openBtnRef } = useBackdropMenu();
  const { isFetching, user } = useCustomer();

  const hasProfileLinks = Array.isArray(headerEntry.profileLinks);
  const SideMenuComponent = hasProfileLinks ? ProfileSideBar : NavBarSideMenu;

  return (
    <>
      <MenuButtonWrapper>
        <NavBarSideMenuOpenButton
          onKeyDown={handleOnKeyDown}
          onClick={openBackdrop}
          label={t('navbar_dropdown_menu_open')}
          isExpanded={backdropOpen}
          ref={openBtnRef}
          hasProfileLinks={hasProfileLinks}
          avatar={{ loading: isFetching, src: user?.images?.[0]?.url || '' }}
        />
      </MenuButtonWrapper>
      {backdropOpen && (
        <Backdrop onClose={closeBackdrop} ref={backdropRef} withPortal dir={dir}>
          <div className={theme}>
            <div className="encore-inverted-dark-set">
              <CloseMenuButton
                size="medium"
                iconOnly={IconX}
                onClick={closeBackdrop}
                onKeyDown={handleOnKeyDown}
                ref={closeBtnRef}
                tabIndex={0}
                aria-label={t('navbar_dropdown_menu_close')}
              />
              <SideMenuComponent
                headerEntry={headerEntry}
                closeBackdrop={closeBackdrop}
                dir={dir}
                username={user?.display_name || user?.id || undefined}
              />
            </div>
          </div>
        </Backdrop>
      )}
    </>
  );
};
